import React, { useState, useEffect } from 'react';
import styled, { keyframes, createGlobalStyle } from 'styled-components';
import { NavigationBar } from './NavigationBar';
import ThreeBackground from './ThreeBackground';
import logoImg from './media/img/bbLogoClear.png';

// Logo imports for conveyor belt
import colgateLogo from './media/img/colgateLogobb.png';
import amfamLogo from './media/img/amfamLogobb.png';
import palmoliveLogo from './media/img/palmoliveLogobb.png';
import seacretLogo from './media/img/seacretLogobb.png';
import classDojLogo from './media/img/classDojLogobbW.png';
import impactiveLogo from './media/img/impactivebb.png';
import bioplusLogo from './media/img/bioplusLogobb.png';
import pmInterLogo from './media/img/pmInterbb.png';
import socialrootsLogo from './media/img/socialrootsbb.png';
import elevateHTLogo from './media/img/elevateHTbb.png';
import patternAgLogo from './media/img/patternAgbb.png';
import zinzinoLogo from './media/img/zinzinoLogobb.png';

// Images are now served from public folder

const CompanyLandingPage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [calendlyLoaded, setCalendlyLoaded] = useState(false);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  // Preload Calendly script on component mount
  useEffect(() => {
    const existingScript = document.querySelector('script[src="https://assets.calendly.com/assets/external/widget.js"]');
    
    if (!existingScript) {
      const script = document.createElement('script');
      script.src = 'https://assets.calendly.com/assets/external/widget.js';
      script.async = true;
      script.onload = () => {
        setCalendlyLoaded(true);
        console.log('Calendly script preloaded successfully');
      };
      script.onerror = () => {
        console.error('Failed to load Calendly script');
      };
      document.head.appendChild(script);
    } else {
      setCalendlyLoaded(true);
    }
  }, []);

  // Initialize Calendly widget when modal opens and script is loaded
  useEffect(() => {
    if (isModalOpen && calendlyLoaded && window.Calendly) {
      const calendlyElement = document.querySelector('.calendly-inline-widget');
      if (calendlyElement) {
        // Re-initialize the widget to ensure proper sizing
        window.Calendly.initInlineWidget({
          url: 'https://calendly.com/bb-global-solutions/discovery',
          parentElement: calendlyElement,
          prefill: {},
          utm: {}
        });
      }
    }
  }, [isModalOpen, calendlyLoaded]);

  // Listen for navigation modal event
  useEffect(() => {
    const handleOpenModal = () => {
      setIsModalOpen(true);
    };

    window.addEventListener('openCalendlyModal', handleOpenModal);
    return () => {
      window.removeEventListener('openCalendlyModal', handleOpenModal);
    };
  }, []);

  // Logo array for conveyor belt
  const logos = [
    colgateLogo,
    amfamLogo,
    palmoliveLogo,
    seacretLogo,
    classDojLogo,
    impactiveLogo,
    bioplusLogo,
    pmInterLogo,
    socialrootsLogo,
    elevateHTLogo,
    patternAgLogo,
    zinzinoLogo,
  ];

  // Render logos for conveyor belt
  const renderLogos = (isSecondSet = false) => {
    return logos.map((logo, index) => (
      <LogoContainer key={`${isSecondSet ? 'dup-' : ''}${index}`}>
        <LogoImage 
          src={logo} 
          alt={`Client Logo ${index + 1}`}
        />
      </LogoContainer>
    ));
  };

  return (
    <>
      <GlobalStyle />
      <ThreeBackground />
      <TransparentNav />
      <PageContainer>
        <HeroSection>
          <HeroContent>
            <CompanyBadge>
              <BadgeIcon>⚡</BadgeIcon>
              NEXT-GEN SOFTWARE
            </CompanyBadge>
            <HeroTitle>
              <AnimatedText>B&B</AnimatedText>
              <GradientText> GLOBAL</GradientText>
              <br />
              <span style={{ color: '#ffffff' }}>SOLUTIONS</span>
            </HeroTitle>
            <HeroSubtitle>
              We are all going to make it! Software for everyone!
            </HeroSubtitle>
            <HeroDescription>
              We create the best custom AI-powered websites for entrepreneurs in 2025, easy-peasy. You hire us, we give you: More time, more freedom and more power for your business
            </HeroDescription>
            <HeroButtons>
              <PrimaryCTA onClick={openModal}>
                <CTAIcon>🚀</CTAIcon>
                Launch Your Vision
              </PrimaryCTA>
              <SecondaryCTA onClick={openModal}>
                <CTAIcon>💫</CTAIcon>
                Start Building
              </SecondaryCTA>
            </HeroButtons>
            <HeroMetrics>
              <MetricItem>
                <MetricNumber>3</MetricNumber>
                <MetricLabel>Fortune-500 clients</MetricLabel>
              </MetricItem>
              <MetricItem>
                <MetricNumber>1</MetricNumber>
                <MetricLabel>Top 10 educational apps in the appstore</MetricLabel>
              </MetricItem>
              <MetricItem>
                <MetricNumber>7</MetricNumber>
                <MetricLabel>years of business</MetricLabel>
              </MetricItem>
            </HeroMetrics>
          </HeroContent>
        </HeroSection>

        {/* Modal */}
        {isModalOpen && (
          <ModalOverlay onClick={closeModal}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
              <ModalHeader>
                <ModalTitle>Book a Discovery Call</ModalTitle>
                <CloseButton onClick={closeModal}>&times;</CloseButton>
              </ModalHeader>
              <CalendlyContainer>
                {!calendlyLoaded && (
                  <CalendlyLoading>
                    <LoadingSpinner />
                    <LoadingText>Loading Calendar...</LoadingText>
                  </CalendlyLoading>
                )}
                <div 
                  className="calendly-inline-widget" 
                  data-url="https://calendly.com/bb-global-solutions/discovery"
                  style={{ 
                    width: '100%', 
                    height: '700px',
                    opacity: calendlyLoaded ? 1 : 0,
                    transition: 'opacity 0.3s ease'
                  }}
                ></div>
              </CalendlyContainer>
            </ModalContent>
          </ModalOverlay>
        )}

        {/* Logo Strip */}
        <LogoStrip>
          <LogoStripTitle>Trusted By Industry Leaders</LogoStripTitle>
          <InfiniteLogoTrack>
            {renderLogos()}
            {renderLogos(true)}
          </InfiniteLogoTrack>
        </LogoStrip>

        {/* Services Overview */}
        <ServicesSection id="solutions">
          <ServicesContainer>
            <SectionTitle>
              <TitleAccent>Core</TitleAccent> Technologies
            </SectionTitle>
            <ServicesGrid>
            <ServiceCard>
              <ServiceIconWrapper>
                <ServiceIcon>🔮</ServiceIcon>
                <ServiceGlow />
              </ServiceIconWrapper>
              <ServiceTitle>AI, AI & AI</ServiceTitle>
              <ServiceDescription>
                Want chatgpt, or any of the popular LLMs in your website? We can make it happen. AI employees? Got em. Custom AI solutions? YES!
              </ServiceDescription>
              <ServiceTech>
                <TechChip>OpenAI</TechChip>
                <TechChip>Claude</TechChip>
                <TechChip>Nvidia</TechChip>
              </ServiceTech>
            </ServiceCard>
            <ServiceCard>
              <ServiceIconWrapper>
                <ServiceIcon>⚛️</ServiceIcon>
                <ServiceGlow />
              </ServiceIconWrapper>
              <ServiceTitle>Modern Spectacular Webapps</ServiceTitle>
              <ServiceDescription>
                Cutting edge websites, built in the way the top technology companies in the US do it in 2025
              </ServiceDescription>
              <ServiceTech>
                <TechChip>React</TechChip>
                <TechChip>Next.js</TechChip>
                <TechChip>Node</TechChip>
              </ServiceTech>
            </ServiceCard>
            <ServiceCard>
              <ServiceIconWrapper>
                <ServiceIcon>🌐</ServiceIcon>
                <ServiceGlow />
              </ServiceIconWrapper>
              <ServiceTitle>Business Centered Development</ServiceTitle>
              <ServiceDescription>
                Now is the time! This is your moment to make that idea come true and have fun making spectacular customized solutions for your business!
              </ServiceDescription>
              <ServiceTech>
                <TechChip>Let's</TechChip>
                <TechChip>Fucking</TechChip>
                <TechChip>Go</TechChip>
              </ServiceTech>
            </ServiceCard>

          </ServicesGrid>
          </ServicesContainer>
        </ServicesSection>

        {/* Mbappe Section */}
        <MbappeSection id="mbappe">
          <MbappeContainer>
            <MbappeTitle>The Mbappé of Software Development</MbappeTitle>
            <MbappeDescription>
              Just like Kylian Mbappé dominates the football field with lightning speed, 
              precision, and game-changing performances, B&B Global Solutions leads the 
              software development arena with unmatched agility, cutting-edge innovation, 
              and transformative solutions that score big for businesses worldwide.
            </MbappeDescription>
            
            <MbappeCardsContainer>
              <TradingCard>
                <CardImage src="/mbappe1.jpg" alt="Kylian Mbappé" />
                <CardOverlay>
                  <CardHeader>
                    <CardTitle className="golden">Kylian Mbappé</CardTitle>
                    <CardSubtitle>Football Legend</CardSubtitle>
                  </CardHeader>
                  <CardNumber>#025</CardNumber>
                  <NumberBadge>
                    <ServiceImage src="/rmEscudo.png" alt="RM Badge" />
                  </NumberBadge>
                  <TopCornerBadge>
                    <ServiceImage src="/balon.png" alt="Balon Badge" />
                  </TopCornerBadge>
                  <DiagonalAccent />
                  <CardStats>
                    <StatItem>
                      <StatLabel>Speed</StatLabel>
                      <StatValue>99/100</StatValue>
                    </StatItem>
                    <StatItem>
                      <StatLabel>Goals</StatLabel>
                      <StatValue>300+</StatValue>
                    </StatItem>
                    <StatItem>
                      <StatLabel>Trophies</StatLabel>
                      <StatValue>15+</StatValue>
                    </StatItem>
                    <StatItem>
                      <StatLabel>Rating</StatLabel>
                      <StatValue>⭐⭐⭐⭐⭐</StatValue>
                    </StatItem>
                  </CardStats>
                </CardOverlay>
              </TradingCard>

              <TradingCard>
                <CardImage src="/mbappe2.jpg" alt="B&B Global Solutions Team" />
                <CardOverlay>
                  <CardHeader>
                    <CardTitle className="black-title">B&B Global Solutions</CardTitle>
                    <CardSubtitle>Software Champions</CardSubtitle>
                  </CardHeader>
                  <CardNumber>#0778</CardNumber>
                  <NumberBadge>
                    <ServiceImage src="/rmEscudo.png" alt="RM Badge" />
                  </NumberBadge>
                  <TopCornerBadge>
                    <ServiceImage src="/balon.png" alt="Balon Badge" />
                  </TopCornerBadge>
                  <DiagonalAccent />
                  <CardStats>
                    <StatItem>
                      <StatLabel>Speed</StatLabel>
                      <StatValue>99/100</StatValue>
                    </StatItem>
                    <StatItem>
                      <StatLabel>Projects</StatLabel>
                      <StatValue>500+</StatValue>
                    </StatItem>
                    <StatItem>
                      <StatLabel>Clients</StatLabel>
                      <StatValue>200+</StatValue>
                    </StatItem>
                    <StatItem>
                      <StatLabel>Rating</StatLabel>
                      <StatValue>⭐⭐⭐⭐⭐</StatValue>
                    </StatItem>
                  </CardStats>
                </CardOverlay>
              </TradingCard>
            </MbappeCardsContainer>
          </MbappeContainer>
        </MbappeSection>

        {/* Entrepreneur Solutions Section */}
        <EntrepreneurSection id="entrepreneurs">
          <EntrepreneurContainer>
            <EntrepreneurHeader>
              <EntrepreneurBadge>
                <BadgeIcon>🎯</BadgeIcon>
                TAILORED SOLUTIONS
              </EntrepreneurBadge>
              <EntrepreneurTitle>
                <GradientText>Websites Built for</GradientText>
                <br />
                Every Type of Entrepreneur
              </EntrepreneurTitle>
              <EntrepreneurSubtitle>
                We know your industry. We speak your language. We build exactly what you need.
              </EntrepreneurSubtitle>
            </EntrepreneurHeader>
            
            <EntrepreneurGrid>
              <EntrepreneurCard>
                <EntrepreneurContent>
                  <CardBadge>
                    <BadgeIcon>🚀</BadgeIcon>
                    FOUNDERS
                  </CardBadge>
                  <EntrepreneurCardTitle>Prototype Builders</EntrepreneurCardTitle>
                  <CardDescription>
                    Need to build and launch fast? We get it. MVP in weeks, not months. 
                    Validate your idea quickly and iterate at lightning speed.
                  </CardDescription>
                  <CardFeatures>
                    <FeatureItem>⚡ Rapid MVP Development</FeatureItem>
                    <FeatureItem>🔄 Quick Iteration Cycles</FeatureItem>
                    <FeatureItem>📊 Built-in Analytics</FeatureItem>
                  </CardFeatures>
                </EntrepreneurContent>
                <EntrepreneurVisual>
                  <img src="/founder.gif" alt="Founder working on startup" />
                </EntrepreneurVisual>
              </EntrepreneurCard>

              <EntrepreneurCard>
                <EntrepreneurContent>
                  <CardBadge>
                    <BadgeIcon>⚡</BadgeIcon>
                    TECH LEADERS
                  </CardBadge>
                  <EntrepreneurCardTitle>Fast-Track Development</EntrepreneurCardTitle>
                  <CardDescription>
                    Skip the hiring headaches. Work directly with us to build faster prototypes 
                    without managing designers and developers. Stay involved, ship faster.
                  </CardDescription>
                  <CardFeatures>
                    <FeatureItem>🎯 Direct Collaboration</FeatureItem>
                    <FeatureItem>⚡ No Team Management</FeatureItem>
                    <FeatureItem>🛠️ Technical Partnership</FeatureItem>
                  </CardFeatures>
                </EntrepreneurContent>
                <EntrepreneurVisual>
                  <img src="/tech-leader.gif" alt="Tech leader developing" />
                </EntrepreneurVisual>
              </EntrepreneurCard>

              <EntrepreneurCard>
                <EntrepreneurContent>
                  <CardBadge>
                    <BadgeIcon>🔧</BadgeIcon>
                    LOCAL SERVICES
                  </CardBadge>
                  <EntrepreneurCardTitle>Plumbing, HVAC & Landscaping</EntrepreneurCardTitle>
                  <CardDescription>
                    Service-based business websites that actually convert. Online booking, 
                    customer management, and local SEO that gets you found by customers.
                  </CardDescription>
                  <CardFeatures>
                    <FeatureItem>📅 Online Booking System</FeatureItem>
                    <FeatureItem>📍 Local SEO Optimization</FeatureItem>
                    <FeatureItem>📞 Lead Management Tools</FeatureItem>
                  </CardFeatures>
                </EntrepreneurContent>
                <EntrepreneurVisual>
                  <img src="/local-services.gif" alt="Local services business" />
                </EntrepreneurVisual>
              </EntrepreneurCard>

              <EntrepreneurCard>
                <EntrepreneurContent>
                  <CardBadge>
                    <BadgeIcon>💫</BadgeIcon>
                    PASSIONPRENEURS
                  </CardBadge>
                  <EntrepreneurCardTitle>Coaches & Instructors</EntrepreneurCardTitle>
                  <CardDescription>
                    Turn your passion into profit. Websites for coaches, yoga/pilates instructors, 
                    and life coaches with booking, payments, and client management built-in.
                  </CardDescription>
                  <CardFeatures>
                    <FeatureItem>💳 Payment Integration</FeatureItem>
                    <FeatureItem>👥 Client Portal</FeatureItem>
                    <FeatureItem>📧 Email Marketing</FeatureItem>
                  </CardFeatures>
                </EntrepreneurContent>
                <EntrepreneurVisual>
                  <img src="/passionpreneur.gif" alt="Coach or instructor" />
                </EntrepreneurVisual>
              </EntrepreneurCard>

              <EntrepreneurCard>
                <EntrepreneurContent>
                  <CardBadge>
                    <BadgeIcon>🎨</BadgeIcon>
                    CREATIVES
                  </CardBadge>
                  <EntrepreneurCardTitle>Photographers, Painters & Crafters</EntrepreneurCardTitle>
                  <CardDescription>
                    Showcase your art beautifully. Portfolio websites with e-commerce, 
                    client galleries, and booking systems that let your creativity shine.
                  </CardDescription>
                  <CardFeatures>
                    <FeatureItem>🖼️ Portfolio Showcase</FeatureItem>
                    <FeatureItem>🛒 E-commerce Integration</FeatureItem>
                    <FeatureItem>📸 Client Gallery Access</FeatureItem>
                  </CardFeatures>
                </EntrepreneurContent>
                <EntrepreneurVisual>
                  <img src="/creative.gif" alt="Creative artist working" />
                </EntrepreneurVisual>
              </EntrepreneurCard>
            </EntrepreneurGrid>
          </EntrepreneurContainer>
        </EntrepreneurSection>

        {/* MLM Section */}
        <MLMSection>
          <MLMContainer>
            <MLMContent>
            <MLMBadge>
              <BadgeIcon>💎</BadgeIcon>
              SPECIALIZED EXPERTISE
            </MLMBadge>
            <MLMTitle>
              <GradientText>Multi-Level Marketing</GradientText>
              <br />
              Platform Solutions
            </MLMTitle>
            <MLMDescription>
              We architect sophisticated MLM ecosystems that scale globally. Our platforms handle 
              complex commission structures, real-time analytics, and seamless user experiences.
            </MLMDescription>
            <MLMFeatures>
              <MLMFeature>
                <FeatureIcon>⚡</FeatureIcon>
                <FeatureText>Smart Contract Commission Systems</FeatureText>
              </MLMFeature>
              <MLMFeature>
                <FeatureIcon>📱</FeatureIcon>
                <FeatureText>Mobile-First Distributor Apps</FeatureText>
              </MLMFeature>
              <MLMFeature>
                <FeatureIcon>🔗</FeatureIcon>
                <FeatureText>Blockchain-Verified Transactions</FeatureText>
              </MLMFeature>
              <MLMFeature>
                <FeatureIcon>🎯</FeatureIcon>
                <FeatureText>AI-Driven Lead Generation</FeatureText>
              </MLMFeature>
            </MLMFeatures>
            <MLMButton href="/direct">
              <ButtonIcon>🚀</ButtonIcon>
              Explore MLM Solutions
              <ButtonArrow>→</ButtonArrow>
            </MLMButton>
          </MLMContent>
          <MLMVisual>
            <NetworkContainer>
              <NetworkNode primary>
                <NodeIcon>👑</NodeIcon>
                <NodeLabel>YOU</NodeLabel>
              </NetworkNode>
              <NetworkNode style={{ top: '10%', right: '20%' }}>
                <NodeIcon>💼</NodeIcon>
                <NodeLabel>A</NodeLabel>
              </NetworkNode>
              <NetworkNode style={{ top: '30%', right: '10%' }}>
                <NodeIcon>🎯</NodeIcon>
                <NodeLabel>B</NodeLabel>
              </NetworkNode>
              <NetworkNode style={{ bottom: '30%', right: '10%' }}>
                <NodeIcon>💎</NodeIcon>
                <NodeLabel>C</NodeLabel>
              </NetworkNode>
              <NetworkNode style={{ bottom: '10%', right: '20%' }}>
                <NodeIcon>⚡</NodeIcon>
                <NodeLabel>D</NodeLabel>
              </NetworkNode>
              <NetworkNode style={{ bottom: '30%', left: '10%' }}>
                <NodeIcon>🔥</NodeIcon>
                <NodeLabel>E</NodeLabel>
              </NetworkNode>
              <NetworkNode style={{ top: '30%', left: '10%' }}>
                <NodeIcon>🌟</NodeIcon>
                <NodeLabel>F</NodeLabel>
              </NetworkNode>
              <NetworkConnections />
            </NetworkContainer>
          </MLMVisual>
          </MLMContainer>
        </MLMSection>

        {/* Why Choose Us */}
        <WhyChooseSection id="why-choose">
          <WhyChooseContainer>
            <SectionTitle>
              <TitleAccent>Why Choose</TitleAccent> B&B Global Solutions?
            </SectionTitle>
            <WhyChooseGrid>
            <WhyChooseItem>
              <WhyChooseNumber>7+</WhyChooseNumber>
              <WhyChooseLabel>Years of Innovation</WhyChooseLabel>
              <WhyChooseIcon>🚀</WhyChooseIcon>
            </WhyChooseItem>
            <WhyChooseItem>
              <WhyChooseNumber>100+</WhyChooseNumber>
              <WhyChooseLabel>Projects Launched</WhyChooseLabel>
              <WhyChooseIcon>💫</WhyChooseIcon>
            </WhyChooseItem>

            <WhyChooseItem>
              <WhyChooseNumber>100%</WhyChooseNumber>
              <WhyChooseLabel>Client Success</WhyChooseLabel>
              <WhyChooseIcon>🎯</WhyChooseIcon>
            </WhyChooseItem>

            <WhyChooseItem>
              <WhyChooseNumber>1 Week</WhyChooseNumber>
              <WhyChooseLabel>Progress in 1 week, or your money back</WhyChooseLabel>
              <WhyChooseIcon>⚡</WhyChooseIcon>
            </WhyChooseItem>

            <WhyChooseItem>
              <WhyChooseNumber>$0</WhyChooseNumber>
                              <WhyChooseLabel>Down, Pay in quotes, no credit needed</WhyChooseLabel>
              <WhyChooseIcon>💳</WhyChooseIcon>
            </WhyChooseItem>
          </WhyChooseGrid>
          </WhyChooseContainer>
        </WhyChooseSection>

        {/* Call to Action */}
        <CTASection id="contact">
          <CTAContainer>
            <CTAContent>
            <CTATitle>
              Ready to <GradientText>Transform</GradientText> Your Business?
            </CTATitle>
            <CTADescription>
              Join the next generation of businesses leveraging cutting-edge technology. 
              Let's architect your digital future together.
            </CTADescription>
            <CTAButtons>
              <CTAPrimary href="tel:313-509-8414">
                <CTAIcon>📞</CTAIcon>
                Call Now: 313-509-8414
              </CTAPrimary>
              <CTASecondary as="button" onClick={openModal}>
                <CTAIcon>📅</CTAIcon>
                Book a Call
              </CTASecondary>
            </CTAButtons>
          </CTAContent>
          </CTAContainer>
        </CTASection>

        <GlassFooter>
          <div className="footer-logo">
            <img src={logoImg} alt="B&B Global Solutions" />
          </div>
          <div className="footer-description">
            "B&B Global Solutions sells time." Time you save improving your business, time ahead of market trends and competitors, 
            and time for you to enjoy doing whatever you decide to do with it.
          </div>
          <div className="footer-contact">
            <a href="tel:313-509-8414">📞 Call: 313-509-8414</a>
            <a href="mailto:contact@bb-global-solutions.com">✉️ Contact Us</a>
          </div>
          <div className="footer-copyright">
            © 2024 B&B Global Solutions. All rights reserved. | Revolutionizing business technology.
          </div>
        </GlassFooter>
      </PageContainer>
    </>
  );
};

// Animations
const float = keyframes`
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-20px) rotate(5deg); }
`;

const glow = keyframes`
  0%, 100% { box-shadow: 0 0 20px rgba(120, 119, 198, 0.6); }
  50% { box-shadow: 0 0 40px rgba(120, 119, 198, 0.8), 0 0 60px rgba(255, 119, 198, 0.4); }
`;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(50px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(120, 119, 198, 0.7);
  }
  50% {
    transform: scale(1.05);
    box-shadow: 0 0 0 20px rgba(120, 119, 198, 0);
  }
`;

const shimmer = keyframes`
  0% { background-position: -200px 0; }
  100% { background-position: calc(200px + 100%) 0; }
`;

const subtleGlow = keyframes`
  0%, 100% { text-shadow: 0 0 2px #7877c6, 0 0 4px #7877c6; }
  50% { text-shadow: 0 0 4px #7877c6, 0 0 8px #7877c6; }
`;

const rotate = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

// Styled Components
const GlobalStyle = createGlobalStyle`
  body {
    margin: 0;
    padding: 0;
    font-family: 'Inter', sans-serif;
    background: #0a0a0a;
    color: #ffffff;
    overflow-x: hidden;
  }
  * {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }
  h1, h2, h3, h4, h5, h6 {
    font-family: 'Space Grotesk', sans-serif;
  }
  a {
    color: #7877c6;
    text-decoration: none;
    transition: color 0.3s ease;
  }
  a:hover {
    color: #ff77c6;
  }
  button {
    cursor: pointer;
    border: none;
    outline: none;
  }
  input, textarea {
    font-family: 'Inter', sans-serif;
  }
  ::-webkit-scrollbar {
    width: 8px;
  }
  ::-webkit-scrollbar-track {
    background: #1a1a1a;
  }
  ::-webkit-scrollbar-thumb {
    background: #7877c6;
    border-radius: 4px;
  }
  ::-webkit-scrollbar-thumb:hover {
    background: #ff77c6;
  }
`;

const TransparentNav = styled(NavigationBar)`
  position: fixed;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
  background: rgba(15, 15, 35, 0.1);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(139, 92, 246, 0.2);
  border-radius: 0 0 20px 20px;
  padding: 1rem 2rem;
  width: 90%;
  max-width: 1100px;
  
  @media (min-width: 1400px) {
    width: 85%;
    max-width: 1200px;
  }
  
  @media (max-width: 768px) {
    width: 98%;
    padding: 0.8rem 1rem;
  }
`;

const PageContainer = styled.div`
  width: 100%;
  overflow-x: hidden;
  position: relative;
  z-index: 1;
  padding-top: 80px; /* Add space for fixed navbar */
  
  @media (max-width: 768px) {
    padding-top: 70px;
  }
`;

const HeroSection = styled.section`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 90vh;
  padding: 2rem;
  position: relative;
  max-width: 1200px;
  margin: 0 auto;

  @media (min-width: 1200px) {
    min-height: 85vh;
    padding: 3rem 2rem;
  }

  @media (max-width: 991px) {
    flex-direction: column;
    padding: 100px 40px 60px;
    text-align: center;
    max-width: none;
  }

  @media (max-width: 768px) {
    padding: 80px 20px 40px;
  }
`;

const HeroContent = styled.div`
  max-width: 650px;
  width: 100%;
  z-index: 10;
  position: relative;
  animation: ${fadeInUp} 1s ease-out;
  background: rgba(15, 15, 35, 0.15);
  backdrop-filter: blur(25px);
  border: 1px solid rgba(139, 92, 246, 0.3);
  border-radius: 20px;
  padding: 3rem;
  box-shadow: 0 8px 32px rgba(139, 92, 246, 0.1);
  margin: 0 auto;
  
  @media (min-width: 1200px) {
    max-width: 700px;
    padding: 3.5rem;
  }
  
  @media (max-width: 768px) {
    padding: 2rem;
    margin: 1rem;
    max-width: 90%;
  }
`;

const CompanyBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(120, 119, 198, 0.1);
  color: #7877c6;
  padding: 12px 24px;
  border-radius: 30px;
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: 32px;
  backdrop-filter: blur(20px);
  border: 1px solid rgba(120, 119, 198, 0.3);
  animation: ${glow} 3s ease-in-out infinite;
`;

const BadgeIcon = styled.span`
  font-size: 1.2rem;
  animation: ${rotate} 10s linear infinite;
`;

const HeroTitle = styled.h1`
  font-size: 4rem;
  font-weight: 900;
  color: white;
  line-height: 1.1;
  margin-bottom: 24px;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
  font-family: 'Space Grotesk', sans-serif;

  @media (min-width: 1200px) {
    font-size: 3.8rem;
  }

  @media (max-width: 768px) {
    font-size: 3rem;
  }
`;

const AnimatedText = styled.span`
  background: linear-gradient(45deg, #7877c6, #ff77c6, #7877c6);
  background-size: 200% 200%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: ${shimmer} 3s ease-in-out infinite;
  display: inline-block;
  text-shadow: none;
`;

const GradientText = styled.span`
  background: linear-gradient(45deg, #7877c6, #ff77c6);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-weight: 900;
`;

const HeroSubtitle = styled.h2`
  font-size: 1.6rem;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 600;
  margin-bottom: 20px;
  line-height: 1.4;
  font-family: 'Space Grotesk', sans-serif;

  @media (max-width: 768px) {
    font-size: 1.3rem;
  }
`;

const HeroDescription = styled.p`
  font-size: 1.3rem;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.6;
  margin-bottom: 35px;
  max-width: 100%;

  @media (min-width: 1200px) {
    font-size: 1.4rem;
    margin-bottom: 40px;
  }

  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const HeroButtons = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 40px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
`;

const PrimaryCTA = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(45deg, #7877c6, #ff77c6);
  color: white;
  padding: 16px 32px;
  border-radius: 50px;
  text-decoration: none;
  font-weight: 600;
  font-size: 1.1rem;
  transition: all 0.3s ease;
  box-shadow: 0 8px 32px rgba(120, 119, 198, 0.4);
  animation: ${pulse} 2s infinite;
  border: none;
  cursor: pointer;
  font-family: inherit;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 40px rgba(120, 119, 198, 0.6);
    animation: none;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(120, 119, 198, 0.5);
  }
`;

const SecondaryCTA = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  padding: 16px 32px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50px;
  text-decoration: none;
  font-weight: 600;
  font-size: 1.1rem;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  cursor: pointer;
  font-family: inherit;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: translateY(-3px);
    box-shadow: 0 8px 32px rgba(255, 255, 255, 0.2);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.3);
  }
`;

const CTAIcon = styled.span`
  font-size: 1.2rem;
`;

const HeroMetrics = styled.div`
  display: flex;
  gap: 40px;
  margin-top: 20px;

  @media (max-width: 768px) {
    gap: 25px; /* Keep horizontal but with smaller gap */
    justify-content: center;
  }
  
  @media (max-width: 480px) {
    flex-direction: column; /* Stack vertically only on very small screens */
    gap: 15px;
    align-items: center;
  }
`;

const MetricItem = styled.div`
  text-align: center;
`;

const MetricNumber = styled.div`
  font-size: 2rem;
  font-weight: 800;
  color: #7877c6;
  margin-bottom: 4px;
  animation: ${subtleGlow} 3s ease-in-out infinite;
  
  @media (max-width: 768px) {
    font-size: 1.6rem; /* Smaller on tablet screens */
  }
  
  @media (max-width: 480px) {
    font-size: 1.8rem; /* Slightly larger on very small screens for readability */
  }
`;

const MetricLabel = styled.div`
  font-size: 1.1rem;
  color: rgba(255, 255, 255, 0.7);
  font-weight: 500;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.05rem;
  }
`;

const ServicesSection = styled.section`
  padding: 2rem;
  position: relative;
  display: flex;
  justify-content: center;

  @media (max-width: 991px) {
    padding: 2rem 1rem;
  }

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const ServicesContainer = styled.div`
  background: transparent;
  border: 1px solid rgba(139, 92, 246, 0.3);
  border-radius: 20px;
  padding: 4rem 3rem;
  max-width: 1200px;
  width: 100%;
  
  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
  }
`;

const SectionTitle = styled.h2`
  font-size: 3rem;
  font-weight: 800;
  text-align: center;
  color: #ffffff;
  margin-bottom: 80px;
  font-family: 'Space Grotesk', sans-serif;

  @media (max-width: 768px) {
    font-size: 2.2rem;
    margin-bottom: 60px;
  }
`;

const TitleAccent = styled.span`
  color: #7877c6;
  animation: ${subtleGlow} 3s ease-in-out infinite;
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 40px;
  max-width: 1400px;
  margin: 0 auto;
`;

const ServiceCard = styled.div`
  background: rgba(255, 255, 255, 0.02);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: 40px 30px;
  border-radius: 24px;
  text-align: center;
  transition: all 0.3s ease;
  animation: ${fadeInUp} 0.8s ease-out;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(45deg, rgba(120, 119, 198, 0.1), rgba(255, 119, 198, 0.1));
    opacity: 0;
    transition: opacity 0.3s ease;
    z-index: 1;
  }

  &:hover {
    transform: translateY(-15px);
    box-shadow: 0 25px 50px rgba(120, 119, 198, 0.3);
    border-color: rgba(120, 119, 198, 0.5);
    
    &::before {
      opacity: 1;
    }
  }

  > * {
    position: relative;
    z-index: 2;
  }
`;

const ServiceIconWrapper = styled.div`
  position: relative;
  display: inline-block;
  margin-bottom: 24px;
`;

const ServiceIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 0;
  display: block;
  position: relative;
  z-index: 2;
`;

const ServiceImage = styled.img`
  width: 60px;
  height: 60px;
  object-fit: contain;
  margin-bottom: 0;
  display: block;
  position: relative;
  z-index: 2;
  filter: drop-shadow(0 4px 8px rgba(120, 119, 198, 0.3));
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.1);
    filter: drop-shadow(0 6px 12px rgba(120, 119, 198, 0.5));
  }
`;

const ServiceGlow = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 80px;
  height: 80px;
  background: radial-gradient(circle, rgba(120, 119, 198, 0.3), transparent);
  border-radius: 50%;
  animation: ${glow} 3s ease-in-out infinite;
  z-index: 1;
`;

const ServiceTitle = styled.h3`
  font-size: 1.6rem;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 16px;
  font-family: 'Space Grotesk', sans-serif;
`;

const ServiceDescription = styled.p`
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.7;
  font-size: 1rem;
  margin-bottom: 24px;
`;

const ServiceTech = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
`;

const TechChip = styled.span`
  background: rgba(120, 119, 198, 0.2);
  color: #7877c6;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  border: 1px solid rgba(120, 119, 198, 0.3);
  transition: all 0.3s ease;

  &:hover {
    background: rgba(120, 119, 198, 0.3);
    transform: translateY(-2px);
  }
`;

const MLMSection = styled.section`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem;
  position: relative;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const MLMContainer = styled.div`
  display: flex;
  align-items: center;
  background: rgba(15, 15, 35, 0.15);
  backdrop-filter: blur(25px);
  border: 1px solid rgba(139, 92, 246, 0.3);
  border-radius: 20px;
  padding: 4rem 3rem;
  box-shadow: 0 8px 32px rgba(139, 92, 246, 0.1);
  max-width: 1200px;
  width: 100%;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 70% 30%, rgba(120, 119, 198, 0.1), transparent);
    pointer-events: none;
    border-radius: 20px;
  }

  @media (max-width: 991px) {
    flex-direction: column;
    padding: 3rem 2rem;
    text-align: center;
  }

  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
  }
`;

const MLMContent = styled.div`
  flex: 1;
  max-width: 700px;
  margin-right: 80px;
  z-index: 2;

  @media (max-width: 991px) {
    margin-right: 0;
    margin-bottom: 60px;
  }
`;

const MLMBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 119, 198, 0.1);
  color: #ff77c6;
  padding: 12px 24px;
  border-radius: 30px;
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: 32px;
  border: 1px solid rgba(255, 119, 198, 0.3);
  backdrop-filter: blur(10px);
  animation: ${glow} 3s ease-in-out infinite;
`;

const MLMTitle = styled.h2`
  font-size: 3rem;
  font-weight: 800;
  color: white;
  margin-bottom: 24px;
  line-height: 1.2;
  font-family: 'Space Grotesk', sans-serif;

  @media (max-width: 768px) {
    font-size: 2.2rem;
  }
`;

const MLMDescription = styled.p`
  font-size: 1.2rem;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.7;
  margin-bottom: 40px;
  max-width: 600px;
`;

const MLMFeatures = styled.div`
  margin-bottom: 40px;
`;

const MLMFeature = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  padding: 12px 0;
  transition: all 0.3s ease;

  &:hover {
    transform: translateX(10px);
  }
`;

const FeatureIcon = styled.span`
  background: linear-gradient(45deg, #7877c6, #ff77c6);
  color: white;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  font-weight: 600;
  margin-right: 20px;
  box-shadow: 0 4px 15px rgba(120, 119, 198, 0.4);
  animation: ${glow} 3s ease-in-out infinite;
`;

const FeatureText = styled.span`
  color: rgba(255, 255, 255, 0.9);
  font-size: 1.1rem;
  font-weight: 500;
`;

const MLMButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(45deg, #7877c6, #ff77c6);
  color: white;
  padding: 18px 36px;
  border-radius: 50px;
  text-decoration: none;
  font-weight: 600;
  font-size: 1.1rem;
  transition: all 0.3s ease;
  box-shadow: 0 8px 32px rgba(120, 119, 198, 0.4);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s ease;
  }

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 40px rgba(120, 119, 198, 0.6);

    &::before {
      left: 100%;
    }
  }
`;

const ButtonIcon = styled.span`
  font-size: 1.2rem;
`;

const ButtonArrow = styled.span`
  margin-left: 8px;
  transition: transform 0.3s ease;

  ${MLMButton}:hover & {
    transform: translateX(5px);
  }
`;

const MLMVisual = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2;
`;

const NetworkContainer = styled.div`
  position: relative;
  width: 400px;
  height: 400px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const NetworkNode = styled.div`
  position: absolute;
  width: ${props => props.primary ? '100px' : '70px'};
  height: ${props => props.primary ? '100px' : '70px'};
  border-radius: 50%;
  background: ${props => props.primary ? 
    'linear-gradient(45deg, #7877c6, #ff77c6)' : 
    'rgba(255, 255, 255, 0.1)'};
  color: white;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: ${props => props.primary ? '1.2rem' : '0.9rem'};
  border: 2px solid ${props => props.primary ? 
    'transparent' : 
    'rgba(120, 119, 198, 0.5)'};
  animation: ${float} 4s ease-in-out infinite;
  backdrop-filter: blur(10px);
  box-shadow: ${props => props.primary ? 
    '0 8px 32px rgba(120, 119, 198, 0.5)' : 
    '0 4px 16px rgba(120, 119, 198, 0.2)'};

  &:nth-child(1) { animation-delay: 0s; }
  &:nth-child(2) { animation-delay: 0.7s; }
  &:nth-child(3) { animation-delay: 1.4s; }
  &:nth-child(4) { animation-delay: 2.1s; }
  &:nth-child(5) { animation-delay: 2.8s; }
  &:nth-child(6) { animation-delay: 3.5s; }
  &:nth-child(7) { animation-delay: 4.2s; }
`;

const NodeIcon = styled.div`
  font-size: 1.5rem;
  margin-bottom: 4px;
`;

const NodeLabel = styled.div`
  font-size: 0.8rem;
  font-weight: 600;
`;

const NetworkConnections = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
`;

const WhyChooseSection = styled.section`
  padding: 2rem;
  position: relative;
  display: flex;
  justify-content: center;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const WhyChooseContainer = styled.div`
  background: transparent;
  border: 1px solid rgba(139, 92, 246, 0.3);
  border-radius: 20px;
  padding: 4rem 3rem;
  max-width: 1200px;
  width: 100%;
  
  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
  }
`;

const WhyChooseGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 50px;
  max-width: 1000px;
  margin: 0 auto;
`;

const WhyChooseItem = styled.div`
  text-align: center;
  padding: 30px 20px;
  background: rgba(255, 255, 255, 0.02);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(45deg, rgba(120, 119, 198, 0.1), rgba(255, 119, 198, 0.1));
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 40px rgba(120, 119, 198, 0.3);
    border-color: rgba(120, 119, 198, 0.5);

    &::before {
      opacity: 1;
    }
  }

  > * {
    position: relative;
    z-index: 2;
  }
`;

const WhyChooseNumber = styled.div`
  font-size: 3.5rem;
  font-weight: 900;
  background: linear-gradient(45deg, #7877c6, #ff77c6);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 16px;
  font-family: 'Space Grotesk', sans-serif;
  animation: ${subtleGlow} 3s ease-in-out infinite;
`;

const WhyChooseLabel = styled.div`
  font-size: 1.2rem;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 600;
  margin-bottom: 12px;
`;

const WhyChooseIcon = styled.div`
  font-size: 2rem;
  margin-top: 8px;
  animation: ${float} 3s ease-in-out infinite;
`;

const CTASection = styled.section`
  padding: 2rem;
  display: flex;
  justify-content: center;
  position: relative;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const CTAContainer = styled.div`
  text-align: center;
  background: rgba(15, 15, 35, 0.15);
  backdrop-filter: blur(25px);
  border: 1px solid rgba(139, 92, 246, 0.3);
  border-radius: 20px;
  padding: 4rem 3rem;
  box-shadow: 0 8px 32px rgba(139, 92, 246, 0.1);
  max-width: 1200px;
  width: 100%;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 50% 50%, rgba(120, 119, 198, 0.2), transparent);
    pointer-events: none;
    border-radius: 20px;
  }

  @media (max-width: 991px) {
    padding: 3rem 2rem;
  }

  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
  }
`;

const CTAContent = styled.div`
  max-width: 700px;
  margin: 0 auto;
  position: relative;
  z-index: 2;
`;

const CTATitle = styled.h2`
  font-size: 3rem;
  font-weight: 800;
  color: white;
  margin-bottom: 24px;
  line-height: 1.2;
  font-family: 'Space Grotesk', sans-serif;

  @media (max-width: 768px) {
    font-size: 2.2rem;
  }
`;

const CTADescription = styled.p`
  font-size: 1.3rem;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.6;
  margin-bottom: 50px;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const CTAButtons = styled.div`
  display: flex;
  gap: 24px;
  justify-content: center;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
`;

const CTAPrimary = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(45deg, #7877c6, #ff77c6);
  color: white;
  padding: 18px 36px;
  border-radius: 50px;
  text-decoration: none;
  font-weight: 600;
  font-size: 1.1rem;
  transition: all 0.3s ease;
  box-shadow: 0 8px 32px rgba(120, 119, 198, 0.4);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s ease;
  }

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 40px rgba(120, 119, 198, 0.6);

    &::before {
      left: 100%;
    }
  }
`;

const CTASecondary = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  padding: 18px 36px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50px;
  text-decoration: none;
  font-weight: 600;
  font-size: 1.1rem;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  cursor: pointer;
  font-family: inherit;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: translateY(-3px);
    box-shadow: 0 8px 32px rgba(255, 255, 255, 0.2);
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.3);
  }
`;

// New Mbappe Section Styles
const MbappeSection = styled.section`
  position: relative;
  z-index: 10;
  padding: 2rem;
  display: flex;
  justify-content: center;
  text-align: center;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const MbappeContainer = styled.div`
  background: transparent;
  padding: 4rem 3rem;
  max-width: 1200px;
  width: 100%;
  
  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
  }
`;

const MbappeTitle = styled.h2`
  font-size: 3.5rem;
  font-weight: 700;
  background: linear-gradient(135deg, #8b5cf6, #06b6d4, #10b981);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-align: center;
  margin-bottom: 3rem;
  animation: ${shimmer} 3s ease-in-out infinite;
  text-shadow: 
    0 0 10px rgba(139, 92, 246, 0.5),
    0 0 20px rgba(139, 92, 246, 0.3),
    0 0 30px rgba(139, 92, 246, 0.2);
  -webkit-text-stroke: 2px rgba(139, 92, 246, 0.3);
  filter: drop-shadow(0 4px 8px rgba(139, 92, 246, 0.4));
`;

const MbappeCardsContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 4rem;
  position: relative;
  perspective: 1000px;
  height: 500px;
  
  @media (max-width: 768px) {
    height: 450px;
    flex-direction: column;
    gap: 2rem;
  }
`;

const TradingCard = styled.div`
  position: absolute;
  width: 300px;
  height: 420px;
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.4s ease;
  cursor: pointer;
  background: transparent;
  padding: 12px;
  border: 3px solid #8b5cf6;
  box-shadow: 0 8px 32px rgba(139, 92, 246, 0.3);
  aspect-ratio: 300 / 420; /* Maintain consistent aspect ratio */
  
  &:first-child {
    transform: translateX(-120px) rotate(-8deg);
    z-index: 1;
  }
  
  &:last-child {
    transform: translateX(120px) rotate(8deg);
    z-index: 2;
  }
  
  &:hover {
    transform: translateY(-20px) scale(1.05) rotate(0deg) !important;
    box-shadow: 0 25px 50px rgba(139, 92, 246, 0.6);
    z-index: 10;
  }

  @media (max-width: 768px) {
    position: relative;
    width: 300px; /* Keep regular size */
    height: 420px; /* Keep regular proportions */
    padding: 12px;
    
    &:first-child {
      transform: translateY(0) translateX(-60px) rotate(-3deg);
      z-index: 2; /* Top card has higher z-index */
      position: relative;
      margin-bottom: -280px; /* Overlap significantly */
    }
    
    &:last-child {
      transform: translateY(0) translateX(60px) rotate(3deg);
      z-index: 1; /* Bottom card has lower z-index */
      position: relative;
    }
    
    &:hover {
      transform: translateY(-15px) scale(1.03) rotate(0deg) !important;
      z-index: 10;
    }
  }

  @media (max-width: 480px) {
    width: 260px; /* Slightly smaller for very small screens */
    height: 364px; /* Maintain exact ratio 260 * 1.4 */
    padding: 10px;
    
    &:first-child {
      transform: translateY(0) translateX(-40px) rotate(-2deg);
      margin-bottom: -240px; /* Adjust overlap for smaller cards */
    }
    
    &:last-child {
      transform: translateY(0) translateX(40px) rotate(2deg);
    }
  }
`;

const CardImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 4px;
  position: relative;
  z-index: 1;
`;

const CardOverlay = styled.div`
  position: absolute;
  top: 12px;
  left: 12px;
  right: 12px;
  bottom: 12px;
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0.4) 0%,
    rgba(0, 0, 0, 0.1) 40%,
    rgba(0, 0, 0, 0.85) 100%
  );
  border-radius: 4px;
  color: white;
  z-index: 2;

  @media (max-width: 768px) {
    top: 12px;
    left: 12px;
    right: 12px;
    bottom: 12px;
  }
  
  @media (max-width: 480px) {
    top: 10px;
    left: 10px;
    right: 10px;
    bottom: 10px;
  }
`;

const CardHeader = styled.div`
  position: absolute;
  top: 0.8rem;
  left: 0.8rem;
  text-align: left;
  z-index: 3;
  
  @media (max-width: 768px) {
    top: 0.1rem; /* Move very high on smaller screens */
    left: 0.8rem;
  }
`;

const CardTitle = styled.h3`
  font-size: 0.9rem;
  font-weight: 700;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 1px;
  font-family: 'Space Grotesk', sans-serif;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.8);
  line-height: 1.1;
  
  &.golden {
    background: linear-gradient(135deg, #ffd700, #ffed4e, #ffd700);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  
  &.silver {
    background: linear-gradient(135deg, #c0c0c0, #e8e8e8, #c0c0c0);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  
  &.black-title {
    color: #000000 !important;
    background: none !important;
    -webkit-text-fill-color: #000000 !important;
    text-shadow: 0 1px 3px rgba(255, 255, 255, 0.3);
    
    @media (max-width: 768px) {
      font-weight: 900 !important; /* Make BB Global Solutions bolder on mobile */
      text-shadow: 0 2px 4px rgba(255, 255, 255, 0.4);
    }
  }
`;

const CardSubtitle = styled.p`
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.65rem;
  margin: 0.2rem 0 0 0;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.8);
  
  @media (max-width: 768px) {
    margin: 0.15rem 0 0 0; /* Slightly closer spacing on mobile for better alignment */
  }
`;

const CardStats = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;
  background: transparent;
  padding: 0;
  position: absolute;
  bottom: 1rem;
  left: 1rem;
  right: 1rem;
  z-index: 3;
`;

const StatItem = styled.div`
  text-align: center;
  padding: 0.4rem 0.3rem;
  background: rgba(0, 0, 0, 0.6);
  border-radius: 4px;
  border: 1px solid rgba(139, 92, 246, 0.6);
  backdrop-filter: blur(5px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
`;

const StatLabel = styled.div`
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.6rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.1rem;
`;

const StatValue = styled.div`
  color: #8b5cf6;
  font-size: 0.8rem;
  font-weight: 700;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.8);
`;

const CardNumber = styled.div`
  position: absolute;
  top: 0.8rem;
  right: 0.8rem;
  font-size: 0.7rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.6);
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.8);
  z-index: 3;
  font-family: 'Space Grotesk', sans-serif;
`;

const DiagonalAccent = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, rgba(139, 92, 246, 0.3), transparent);
  clip-path: polygon(100% 0%, 0% 0%, 100% 100%);
  z-index: 2;
`;

const NumberBadge = styled.div`
  position: absolute;
  top: 2.5rem;
  right: 0rem;
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.5));
  }
`;

const TopCornerBadge = styled.div`
  position: absolute;
  top: 6.25rem;
  right: 1.4rem;
  width: 35px;
  height: 35px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.5));
  }
`;

const MbappeDescription = styled.p`
  font-size: 1.6rem;
  color: rgba(255, 255, 255, 0.95);
  line-height: 1.8;
  margin: 2rem auto;
  max-width: 900px;
  font-weight: 500;
  text-shadow: 
    0 0 8px rgba(139, 92, 246, 0.6),
    0 0 16px rgba(139, 92, 246, 0.4),
    0 2px 10px rgba(0, 0, 0, 0.5);
  -webkit-text-stroke: 1px rgba(139, 92, 246, 0.2);
  filter: drop-shadow(0 2px 6px rgba(139, 92, 246, 0.3));
  
  @media (max-width: 768px) {
    font-size: 1.4rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.3rem;
  }
`;

// Entrepreneur Solutions Section Styles
const EntrepreneurSection = styled.section`
  padding: 2rem;
  position: relative;
  display: flex;
  justify-content: center;
  background: rgba(15, 15, 35, 0.1);
  backdrop-filter: blur(25px);
  border: 1px solid rgba(139, 92, 246, 0.3);
  border-radius: 20px;
  margin-top: 80px;
  margin-bottom: 80px;
  max-width: 1200px;
  width: 100%;
  margin-left: auto;
  margin-right: auto;

  @media (max-width: 991px) {
    padding: 1rem;
    margin-top: 60px;
    margin-bottom: 60px;
  }

  @media (max-width: 768px) {
    padding: 0.5rem;
    margin-top: 40px;
    margin-bottom: 40px;
  }
`;

const EntrepreneurContainer = styled.div`
  max-width: 1200px;
  width: 100%;
  position: relative;
  z-index: 2;
`;

const EntrepreneurHeader = styled.div`
  text-align: center;
  margin-bottom: 60px;
  animation: ${fadeInUp} 1s ease-out;
`;

const EntrepreneurBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 119, 198, 0.1);
  color: #ff77c6;
  padding: 12px 24px;
  border-radius: 30px;
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: 20px;
  border: 1px solid rgba(255, 119, 198, 0.3);
  backdrop-filter: blur(10px);
  animation: ${glow} 3s ease-in-out infinite;
`;

const EntrepreneurTitle = styled.h2`
  font-size: 3.5rem;
  font-weight: 800;
  color: white;
  margin-bottom: 20px;
  line-height: 1.2;
  font-family: 'Space Grotesk', sans-serif;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);

  @media (max-width: 768px) {
    font-size: 2.5rem;
    margin-bottom: 15px;
  }
`;

const EntrepreneurSubtitle = styled.p`
  font-size: 1.3rem;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.7;
  max-width: 700px;
  margin: 0 auto;
  margin-bottom: 40px;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.5);

  @media (max-width: 768px) {
    font-size: 1.1rem;
    margin-bottom: 30px;
  }
`;

const EntrepreneurGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 60px;
  max-width: 1400px;
  margin: 0 auto;
  align-items: start;
  
  /* Misalign columns on bigger screens */
  @media (min-width: 769px) {
    > *:nth-child(2n) {
      margin-top: 80px;
    }
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 40px;
    
    > *:nth-child(2n) {
      margin-top: 0;
    }
  }
`;

const EntrepreneurCard = styled.div`
  background: rgba(255, 255, 255, 0.02);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: 45px 40px;
  border-radius: 24px;
  text-align: center;
  transition: all 0.3s ease;
  animation: ${fadeInUp} 0.8s ease-out;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(45deg, rgba(120, 119, 198, 0.1), rgba(255, 119, 198, 0.1));
    opacity: 0;
    transition: opacity 0.3s ease;
    z-index: 1;
  }

  &:hover {
    transform: translateY(-15px);
    box-shadow: 0 25px 50px rgba(120, 119, 198, 0.3);
    border-color: rgba(120, 119, 198, 0.5);
    
    &::before {
      opacity: 1;
    }
  }

  > * {
    position: relative;
    z-index: 2;
  }
`;

const EntrepreneurContent = styled.div`
  margin-bottom: 30px;
`;

const CardBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(120, 119, 198, 0.1);
  color: #7877c6;
  padding: 12px 24px;
  border-radius: 30px;
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: 20px;
  border: 1px solid rgba(120, 119, 198, 0.3);
  backdrop-filter: blur(10px);
  animation: ${glow} 3s ease-in-out infinite;
`;

const EntrepreneurCardTitle = styled.h3`
  font-size: 1.8rem;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 16px;
  font-family: 'Space Grotesk', sans-serif;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
`;

const CardDescription = styled.p`
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.7;
  font-size: 1rem;
  margin-bottom: 24px;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.5);
`;

const CardFeatures = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;
  margin-top: 20px;
`;

const FeatureItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(120, 119, 198, 0.1);
  color: rgba(255, 255, 255, 0.9);
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 600;
  backdrop-filter: blur(5px);
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(120, 119, 198, 0.15);
    color: #ffffff;
    transform: translateY(-1px);
  }
`;

const EntrepreneurVisual = styled.div`
  position: relative;
  width: 100%;
  height: 280px; /* Increased height for wider cards */
  background: rgba(120, 119, 198, 0.1);
  border-radius: 15px;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
  box-shadow: 0 8px 32px rgba(120, 119, 198, 0.2);
  border: 1px solid rgba(120, 119, 198, 0.3);
  backdrop-filter: blur(10px);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(45deg, rgba(120, 119, 198, 0.1), rgba(255, 119, 198, 0.1));
    opacity: 0;
    transition: opacity 0.3s ease;
    z-index: 1;
  }

  &:hover {
    &::before {
      opacity: 1;
    }
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    position: relative;
    z-index: 2;
  }
`;

// Glass Footer Component
const GlassFooter = styled.footer`
  background: rgba(15, 15, 35, 0.15);
  backdrop-filter: blur(25px);
  border-top: 1px solid rgba(139, 92, 246, 0.3);
  padding: 4rem 2rem 2rem;
  margin-top: 4rem;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 50% 0%, rgba(139, 92, 246, 0.1), transparent);
    pointer-events: none;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 200px;
    height: 2px;
    background: linear-gradient(90deg, transparent, #7877c6, transparent);
  }
  
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  color: rgba(255, 255, 255, 0.8);
  
  .footer-logo {
    width: 120px;
    height: 120px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.25);
    padding: 15px;
    margin-bottom: 2rem;
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.3);
    transition: all 0.3s ease;
    
    &:hover {
      transform: scale(1.05);
      box-shadow: 0 8px 25px rgba(139, 92, 246, 0.3);
    }
    
    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  }
  
  .footer-description {
    font-size: 1.2rem;
    line-height: 1.8;
    max-width: 600px;
    margin-bottom: 2rem;
    color: rgba(255, 255, 255, 0.9);
  }
  
  .footer-contact {
    display: flex;
    gap: 2rem;
    margin-bottom: 2rem;
    flex-wrap: wrap;
    justify-content: center;
    
    a {
      color: #7877c6;
      text-decoration: none;
      padding: 0.8rem 1.5rem;
      border: 1px solid rgba(120, 119, 198, 0.3);
      border-radius: 25px;
      background: rgba(120, 119, 198, 0.1);
      transition: all 0.3s ease;

      &:hover {
        background: rgba(120, 119, 198, 0.2);
        transform: translateY(-2px);
        box-shadow: 0 5px 15px rgba(120, 119, 198, 0.3);
      }
    }
  }
  
  .footer-copyright {
    padding-top: 2rem;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    color: rgba(255, 255, 255, 0.6);
    font-size: 0.9rem;
    position: relative;
    z-index: 2;
  }

  @media (max-width: 768px) {
    padding: 3rem 1rem 2rem;
    
    .footer-contact {
      flex-direction: column;
      align-items: center;
      gap: 1rem;
    }
  }
`;

// Logo Strip Animations
const scrollInfinite = keyframes`
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-50%);
  }
`;

// Logo Strip Components
const LogoStrip = styled.div`
  width: 100%;
  overflow: hidden;
  white-space: nowrap;
  background: linear-gradient(90deg, 
    rgba(139, 92, 246, 0.05), 
    rgba(139, 92, 246, 0.1),
    rgba(139, 92, 246, 0.15),
    rgba(139, 92, 246, 0.1),
    rgba(139, 92, 246, 0.05)
  );
  border-top: 1px solid rgba(139, 92, 246, 0.2);
  border-bottom: 1px solid rgba(139, 92, 246, 0.2);
  box-shadow: 0 8px 24px rgba(139, 92, 246, 0.1);
  padding: 25px 0;
  position: relative;
  margin: 40px 0;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(90deg, 
      rgba(255, 255, 255, 0) 0%, 
      rgba(255, 255, 255, 0.02) 25%, 
      rgba(255, 255, 255, 0.04) 50%, 
      rgba(255, 255, 255, 0.02) 75%, 
      rgba(255, 255, 255, 0) 100%);
    pointer-events: none;
  }
  
  @media (max-width: 768px) {
    padding: 20px 0;
    margin: 30px 0;
  }
`;

const LogoStripTitle = styled.h3`
  text-align: center;
  font-size: 1.1rem;
  color: #87CEEB;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  margin: 0 0 20px 0;
  font-family: 'Space Grotesk', sans-serif;
`;

const InfiniteLogoTrack = styled.div`
  display: inline-block;
  white-space: nowrap;
  animation: ${scrollInfinite} 40s linear infinite;
  will-change: transform;
`;

const LogoContainer = styled.div`
  display: inline-block;
  margin: 0 30px;
  vertical-align: middle;
`;

const LogoImage = styled.img`
  height: 45px;
  filter: brightness(1) grayscale(15%);
  opacity: 1;
  transition: all 0.3s ease;

  @media (max-width: 768px) {
    height: 35px;
  }

  @media (max-width: 480px) {
    height: 30px;
  }
`;

// Modal Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  backdrop-filter: blur(5px);
`;

const ModalContent = styled.div`
  background: rgba(15, 15, 35, 0.95);
  backdrop-filter: blur(25px);
  border-radius: 20px;
  border: 1px solid rgba(139, 92, 246, 0.3);
  padding: 0;
  max-width: 95vw;
  max-height: 95vh;
  width: 100%;
  max-width: 950px;
  position: relative;
  overflow: hidden;
  box-shadow: 0 25px 50px rgba(139, 92, 246, 0.3);
  display: flex;
  flex-direction: column;
  
  @media (max-width: 768px) {
    max-width: 98vw;
    max-height: 98vh;
    margin: 1vh 1vw;
  }
  
  @media (max-width: 480px) {
    max-width: 100vw;
    max-height: 100vh;
    margin: 0;
    border-radius: 0;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 30px;
  border-bottom: 1px solid rgba(139, 92, 246, 0.2);
  background: rgba(139, 92, 246, 0.1);
`;

const ModalTitle = styled.h2`
  color: #ffffff;
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
  font-family: 'Space Grotesk', sans-serif;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #ffffff;
  font-size: 2rem;
  cursor: pointer;
  padding: 0;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    transform: scale(1.1);
  }
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const CalendlyContainer = styled.div`
  padding: 0;
  width: 100%;
  height: 700px;
  position: relative;
  overflow: hidden;
  
  .calendly-inline-widget {
    width: 100% !important;
    height: 700px !important;
    border: none !important;
    border-radius: 0 0 20px 20px !important;
    overflow: hidden !important;
    transition: opacity 0.3s ease;
    
    iframe {
      width: 100% !important;
      height: 700px !important;
      border: none !important;
      border-radius: 0 0 20px 20px !important;
    }
  }
  
  @media (max-width: 768px) {
    height: 600px;
    
    .calendly-inline-widget {
      height: 600px !important;
      
      iframe {
        height: 600px !important;
      }
    }
  }
  
  @media (max-width: 480px) {
    height: 550px;
    border-radius: 0;
    
    .calendly-inline-widget {
      height: 550px !important;
      border-radius: 0 !important;
      
      iframe {
        height: 550px !important;
        border-radius: 0 !important;
      }
    }
  }
`;

const CalendlyLoading = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(15, 15, 35, 0.9);
  backdrop-filter: blur(10px);
  z-index: 10;
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid rgba(139, 92, 246, 0.3);
  border-top: 4px solid #7877c6;
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
  margin-bottom: 16px;
`;

const LoadingText = styled.div`
  color: rgba(255, 255, 255, 0.8);
  font-size: 1rem;
  font-weight: 500;
  text-align: center;
`;

export default CompanyLandingPage; 